<template>
  <b-modal
    v-model="shown"
    id="modal-add-staff"
    :title="modalTitle"
    body-class="staff-selection-modal"
    centered
  >
    <b-input-group class="mb-3">
      <b-form-input
        :placeholder="searchPlaceholder"
        v-model="staffKeyword"
        @input="handleStaffKeywordIsChanged"
      ></b-form-input>
      <b-input-group-append>
        <b-button><i class="fa fa-search mr-0"></i></b-button>
      </b-input-group-append>
    </b-input-group>
    <div class="staff-selection-modal__loading-wrapper" v-if="isLoading">
      <b-spinner style="border-width: 2px" />
    </div>
    <div class="scrolling p-2" v-if="paginatedStaffs.length > 0 && !isLoading">
      <div v-for="staff in paginatedStaffs" v-bind:key="staff.id" class="mb-1">
        <div
          class="d-flex align-items-center p-2 staff-info"
          :class="{ 'staff-info--selected': isSelected(staff) }"
          @click="handleSelected(staff)"
        >
          <b-avatar
            class="mr-2"
            :src="staff.avatar_url"
          ></b-avatar>
          <div
            class="
              mr-auto
              font-weight-bold
              text-nowrap text-truncate
            "
          >
            {{ staff.name }}<br>
            <span class="staff-list-info">
              {{ displayStaffInfo(staff) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selectedStaff"
      class="d-flex align-items-center pt-3"
      style="border-top: 1px solid #ddd;"
    >
      <b-avatar
        class="mr-2"
        :src="selectedStaff.avatar_url"
      ></b-avatar>
      <div
        class="
          mr-auto
          font-weight-bold
          text-nowrap text-truncate
          flex-fill
        "
      >
        {{ selectedStaff.name }}<br>
        <span class="staff-list-info">
          {{ displayStaffInfo(selectedStaff) }}
        </span>
      </div>
      <b-button
        size="xs"
        variant="danger"
        style="border-radius: 50%;"
        class="p-1 mr-2"
        @click="cancelSelected"
      >
        <i class="mdi mdi-close m-0" style="font-size: 14px;"></i>
      </b-button>
    </div>
    <template #modal-footer>
      <div class="w-100 d-flex">
        <div class="pagination mr-auto d-flex align-items-center">
          <a class="mr-2 arrow-point" @click="navigateToPage(1)">
            <IconNavigateToFirst :fill="currentPageNumber === 1 ? '#ccc' : 'black'" />
          </a>
          <a class="mr-2 arrow-point" @click="navigateToPage(currentPageNumber - 1)">
            <IconAngleLeft :fill="currentPageNumber === 1 ? '#ccc' : 'black'" />
          </a>
          <span class="mr-2 d-inline-block page-number" @click.prevent="navigateToPage(currentPageNumber)">
            {{ currentPageNumber }} / {{ totalPages }}
          </span>
          <a class="mr-2 arrow-point" @click="navigateToPage(currentPageNumber + 1)">
            <IconAngleRight :fill="currentPageNumber === totalPages ? '#ccc' : 'black'" />
          </a>
          <a class="arrow-point" @click="navigateToPage(totalPages)">
            <IconNavigateToLast :fill="currentPageNumber === totalPages ? '#ccc' : 'black'" />
          </a>
        </div>

        <b-button variant="danger" size="sm" class="mr-2 ml-3" @click="closeModal">
          取消
        </b-button>
        <b-button variant="primary" size="sm" @click="submitSelection">
          確定
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import IconAngleLeft from '@/components/Icon/IconAngleLeft.vue'
import IconAngleRight from '@/components/Icon/IconAngleRight.vue'
import IconNavigateToLast from '@/components/Icon/IconNavigateToLast.vue'
import IconNavigateToFirst from '@/components/Icon/IconNavigateToFirst.vue'
import _ from 'lodash'

const PAGE_SIZE = 10

export default {
  components: {
    IconAngleLeft,
    IconAngleRight,
    IconNavigateToLast,
    IconNavigateToFirst,
  },
  props: {
    staffs: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    modalTitle: {
      type: String,
      default: '選擇員工',
    },
    searchPlaceholder: {
      type: String,
      default: '請輸入姓名或員工編號',
    },
  },
  data() {
    return {
      selectedStaff: null,
      staffKeyword: null,
      shown: false,
      currentPageNumber: 1,
    }
  },
  computed: {
    filteredStaffs() {
      return this.staffs.filter(staff => {
        return !this.staffKeyword ||
          [
            _.get(staff, 'name') || '',
            _.get(staff, 'employee_code') || ''
          ].some(value => value.includes(this.staffKeyword))
      })
    },
    paginatedStaffs() {
      const start = (this.currentPageNumber - 1) * PAGE_SIZE
      const end = start + PAGE_SIZE
      return this.filteredStaffs.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.filteredStaffs.length / PAGE_SIZE)
    },
  },
  methods: {
    show() {
      this.shown = true
      this.currentPageNumber = 1
    },
    navigateToPage(pageNumber) {
      if (pageNumber < 1 || pageNumber > this.totalPages) {
        return
      }
      this.currentPageNumber = pageNumber
    },
    displayStaffInfo(staff) {
      return ((staff.employee_code) ? staff.employee_code + " " : "") + (staff.email ?? "");
    },
    async submitSelection() {
      if (this.selectedStaff) {
        this.$emit("selected", this.selectedStaff);
        this.shown = false
        this.selectedStaff = null;
      }
    },
    handleStaffKeywordIsChanged() {
      this.currentPageNumber = 1
    },
    handleSelected(e) {
      this.selectedStaff = e;
    },
    isSelected(staff) {
      return this.selectedStaff?.id === staff.id;
    },
    cancelSelected() {
      this.selectedStaff = null
    },
    closeModal() {
      this.shown = false
      this.selectedStaff = null
    }
  }
}
</script>

<style lang="scss" scoped>
@use "@/assets/scss/base-colors" as variables;

::v-deep .staff-selection-modal {
  height: 60vh;
  display: flex;
  flex-direction: column;

  &__loading-wrapper {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .staff-list-info {
    font-size: 0.75rem;
    color: gray;
    font-weight: 400;
  }

  .scrolling {
    overflow-y: scroll;
    height: 50vh;
  }

  .staff-info {
    border-radius: 8px;
    position: relative;

    &--selected, &.staff-info--selected:hover {
      outline: 1px solid lighten(variables.$green, 20%);
      color: unset;
      background-color: lighten(variables.$teal, 35%);

      &:after {
        content: '已選擇';
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 0.75rem;
        color: variables.$green;
      }

      .staff-list-info {
        color: gray;
      }
    }

    &:hover {
      cursor: pointer;
      background-color: #efefef;
      color: unset;
      width: 100%;

      .staff-list-info {
        color: gray;
      }
    }
  }

  .arrow-point:hover {
    cursor: pointer;
  }

  .page-number {
    color: #888;
    min-width: 3rem;
    text-align: center;
  }
}
</style>
